/**
 * router/index.ts — Vue Router configuration
 *
 * Defines all application routes with lazy-loaded views.
 * Auth guard redirects unauthenticated users to /auth.
 * Profile guard redirects users without a profile to /profile.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
    /* -------------------------------------------------- */
    /* Auth routes (no sidebar, centered layout)           */
    /* -------------------------------------------------- */
    {
        path: '/auth',
        component: () => import('@/layouts/AuthLayout.vue'),
        children: [
            {
                path: '',
                name: 'auth-select',
                component: () => import('@/views/auth/AuthSelect.vue'),
            },
            {
                path: 'email',
                name: 'auth-email',
                component: () => import('@/views/auth/AuthEmail.vue'),
            },
            {
                path: 'web3',
                name: 'auth-web3',
                component: () => import('@/views/auth/AuthWeb3.vue'),
            },
        ],
    },

    /* -------------------------------------------------- */
    /* Profile setup (post-auth, before full app access)   */
    /* -------------------------------------------------- */
    {
        path: '/profile',
        component: () => import('@/layouts/AuthLayout.vue'),
        children: [
            {
                path: '',
                name: 'profile-setup',
                component: () => import('@/views/profile/ProfileSetup.vue'),
                meta: { requiresAuth: true },
            },
        ],
    },

    /* -------------------------------------------------- */
    /* App routes (sidebar layout, requires auth)          */
    /* -------------------------------------------------- */
    {
        path: '/',
        component: () => import('@/layouts/AppLayout.vue'),
        meta: { requiresAuth: true, requiresProfile: true },
        children: [
            {
                path: '',
                name: 'dashboard',
                component: () => import('@/views/dashboard/DashboardHome.vue'),
            },
            {
                path: 'offers',
                name: 'offers-list',
                component: () => import('@/views/offers/OffersList.vue'),
            },
            {
                path: 'offers/create',
                name: 'offer-create',
                component: () => import('@/views/offers/OfferCreate.vue'),
            },
            {
                path: 'offers/:id',
                name: 'offer-detail',
                component: () => import('@/views/offers/OfferDetail.vue'),
            },
            {
                path: 'trades',
                name: 'trades-list',
                component: () => import('@/views/trades/TradesList.vue'),
            },
            {
                path: 'trades/:id',
                name: 'trade-view',
                component: () => import('@/views/trades/TradeView.vue'),
            },
            {
                path: 'wallet',
                name: 'wallet',
                component: () => import('@/views/wallet/WalletView.vue'),
            },
            {
                path: 'messages',
                name: 'messages',
                component: () => import('@/views/messages/MessagesView.vue'),
            },
            {
                path: 'settings',
                name: 'settings',
                component: () => import('@/views/settings/SettingsView.vue'),
            },
        ],
    },

    /* -------------------------------------------------- */
    /* Catch-all redirect                                  */
    /* -------------------------------------------------- */
    {
        path: '/:pathMatch(.*)*',
        redirect: '/',
    },
];

export const router = createRouter({
    history: createWebHistory(),
    routes,
});

/* -------------------------------------------------- */
/* Navigation guards                                   */
/* -------------------------------------------------- */

/** Track whether we've restored the session on first navigation */
let sessionRestored = false;

router.beforeEach(async (to, _from, next) => {
    const auth = useAuthStore();

    /**
     * Restore persisted session on first navigation.
     * This runs once — subsequent navigations use in-memory state.
     */
    if (!sessionRestored) {
        sessionRestored = true;
        const restored = auth.restore();

        /**
         * If a session was restored, validate it against the API.
         * If the API says it's invalid/expired, clear and redirect.
         */
        if (restored && auth.sessionToken) {
            try {
                const { useApi } = await import('@/composables/useApi');
                const api = useApi();
                const me = await api.get<{
                    email: string;
                    role: string;
                    displayName: string | null;
                    isNewUser: boolean;
                    expiresAt: string;
                }>(
                    '/auth/me',
                    auth.sessionToken,
                );

                /**
                 * Sync server-side profile state with client.
                 * If the server says the user still needs onboarding,
                 * update the local state accordingly.
                 */
                if (me.isNewUser) {
                    auth.isNewUser = true;
                    localStorage.removeItem('localusdt_profile');
                } else {
                    auth.isNewUser = false;
                    localStorage.setItem('localusdt_profile', 'true');
                }

                /** Debug: log successful session validation */
                console.log('[ROUTER] Persisted session validated via API');
            } catch {
                /** Session is invalid on the server — clear it */
                console.log('[ROUTER] Persisted session invalid — clearing');
                auth.logout();
            }
        }
    }

    const isAuthenticated = auth.isAuthenticated;
    const hasProfile = !auth.isNewUser;

    /** Debug: log navigation guard checks */
    console.log(`[ROUTER] Navigating to ${to.path} | auth=${isAuthenticated} profile=${hasProfile}`);

    if (to.meta.requiresAuth && !isAuthenticated) {
        console.log('[ROUTER] Redirecting to /auth — not authenticated');
        return next({ name: 'auth-select' });
    }

    if (to.meta.requiresProfile && isAuthenticated && !hasProfile) {
        console.log('[ROUTER] Redirecting to /profile — no profile');
        return next({ name: 'profile-setup' });
    }

    /** If already authenticated and going to auth pages, redirect to dashboard */
    if (to.path.startsWith('/auth') && isAuthenticated) {
        if (hasProfile) {
            console.log('[ROUTER] Already authenticated — redirecting to dashboard');
            return next({ name: 'dashboard' });
        } else {
            console.log('[ROUTER] Authenticated but no profile — redirecting to /profile');
            return next({ name: 'profile-setup' });
        }
    }

    next();
});
